import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useToast } from '../../../../components/ui/Toast';
import { Decimal } from 'decimal.js';
import { useCreateDecree } from '../../../../features/finance/hooks';
import { SubscriberSearchInput, type SubscriberSearchResult } from '../../../../features/finance/components/SubscriberSearchInput';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/finance/decrees/new')({
  component: LogLegalDecreePage,
});

const decreeSchema = z.object({
  caseNumber: z.string().min(1, 'Case number is required'),
  subscriberId: z.string().min(1, 'Subscriber is required'),
  ticketNumber: z.string().optional(),
  awardedRupees: z.string().min(1, 'Awarded amount is required'),
  courtFeesRupees: z.string().optional(),
  hearingDate: z.string().min(1, 'Hearing date is required'),
  status: z.string(),
  notes: z.string().max(500).optional(),
});

type DecreeForm = z.infer<typeof decreeSchema>;

const decreeFormConfig = defineFormPage<DecreeForm>({
  resource: 'decrees',
  defaultValues: {
    caseNumber: '',
    subscriberId: '',
    ticketNumber: '',
    awardedRupees: '',
    courtFeesRupees: '',
    hearingDate: '',
    status: 'FILING',
    notes: '',
  },
  schema: decreeSchema,
});

function LogLegalDecreePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateDecree();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(decreeFormConfig, createMutation, {
    routeId: 'finance/decrees/new',
    onBeforeSubmit: (value) => {
      const award = new Decimal(value.awardedRupees);
      if (award.isNaN() || award.lte(0)) throw new Error('Please enter a valid awarded amount.');
      const fees = new Decimal(value.courtFeesRupees || '0');
      return {
        decreeNumber: value.caseNumber.toUpperCase(),
        subscriberId: value.subscriberId,
        ticketNumber: value.ticketNumber ? parseInt(value.ticketNumber, 10) : undefined,
        amountPaise: award.mul(100).round().toNumber(),
        courtFeesPaise: fees.mul(100).round().toNumber(),
        decreeDate: value.hearingDate,
        status: value.status,
        notes: value.notes || undefined,
      } as never;
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Decree Recorded',
        description: 'Arbitration/Legal decree awards logged and added to debtor interest-accrual watchlists.',
      });
      void navigate({ to: '/finance/decrees' });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Failed to Log Decree',
        description: err?.message || 'Could not record the decree case. Please try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Record Decree"
      subtitle="Record a court decree fine amount against a defaulted member per chit agreement rules."
      backTo="/finance/decrees"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="caseNumber"
                validators={{ onChange: ({ value }: { value: any }) => decreeSchema.shape.caseNumber.safeParse(value).success ? undefined : decreeSchema.shape.caseNumber.safeParse(value).error?.issues[0]?.message }}
                children={(field: any) => (
                  <FormField label="Decree/Case Index No." required>
                    <Input
                      placeholder="e.g. ARB-2026-4402"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="subscriberId"
                validators={{ onChange: ({ value }: { value: any }) => decreeSchema.shape.subscriberId.safeParse(value).success ? undefined : decreeSchema.shape.subscriberId.safeParse(value).error?.issues[0]?.message }}
                children={(field: any) => (
                  <FormField label="Subscriber" required>
                    <SubscriberSearchInput
                      value={field.state.value}
                      onChange={(subscriberId: string, subscriber?: SubscriberSearchResult) => {
                        field.handleChange(subscriberId);
                        if (subscriber && subscriber.ticketNumbers.length > 0) {
                          form.setFieldValue('ticketNumber' as never, String(subscriber.ticketNumbers[0]) as never);
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="ticketNumber"
                children={(field: any) => (
                  <FormField label="Ticket No. under dispute">
                    <Input
                      placeholder="e.g. 12"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="awardedRupees"
                validators={{ onChange: ({ value }: { value: any }) => decreeSchema.shape.awardedRupees.safeParse(value).success ? undefined : decreeSchema.shape.awardedRupees.safeParse(value).error?.issues[0]?.message }}
                children={(field: any) => (
                  <FormField label="Awarded Decree Principal (₹)" required>
                    <Input
                      type="number"
                      placeholder="e.g. 150000"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="courtFeesRupees"
                children={(field: any) => (
                  <FormField label="Legal & Court Fees Paid (₹)">
                    <Input
                      type="number"
                      placeholder="e.g. 7500"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="hearingDate"
                validators={{ onChange: ({ value }: { value: any }) => decreeSchema.shape.hearingDate.safeParse(value).success ? undefined : decreeSchema.shape.hearingDate.safeParse(value).error?.issues[0]?.message }}
                children={(field: any) => (
                  <FormField label="Next Hearing / Award Date" required>
                    <Input
                      type="date"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            </div>

            <form.Field
              name="status"
              children={(field: any) => (
                <FormSelect
                  label="Decree Recovery Stage"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e: any) => field.handleChange(e.target.value as any)}
                  options={[
                    { value: 'FILING', label: 'Filing Stage (Pre-Decree)' },
                    { value: 'EXECUTION', label: 'Execution Court Stage (EP Filed)' },
                    { value: 'APPEALED', label: 'Appealed in High Court' },
                    { value: 'SETTLED', label: 'Settled in Full' },
                  ]}
                  disabled={isSubmitting}
                />
              )}
            />

            <form.Field
              name="notes"
              children={(field: any) => (
                <FormField label="Additional Notes">
                  <textarea
                    className="w-full h-24 p-3 border border-border rounded-lg bg-surface-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent-500"
                    placeholder="Enter decree details, legal notes or status tracking details..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e: any) => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/finance/decrees' })} isSubmitting={isSubmitting} submitLabel="Log Decree" />
          </form>
    </FormPageLayout>
  );
}
